/** La vista de tabla: una fila por registro, una columna por propiedad, todo editable en el sitio. */
import { useRef, useState } from 'react'

import { IconoDePagina } from '../Arbol'
import { Flotante, Menu } from '../Menu'
import type { AccionesDeVista } from './BaseVista'
import { Celda } from './Celdas'
import { propiedadNueva } from './logica'
import { COLORES, ICONO_DE_TIPO, TIPOS, type Esquema, type Propiedad, type TipoProp } from './tipos'

export function Tabla(p: AccionesDeVista) {
  const { esquema, vista, filas, editable } = p
  const ocultas = new Set(vista.hidden ?? [])
  const columnas = esquema.properties.filter((c) => !ocultas.has(c.id))
  const [añadiendo, setAñadiendo] = useState(false)
  const botonAñadir = useRef<HTMLButtonElement>(null)

  return (
    <div className="tabla-marco">
      <table className="tabla" role="grid">
        <thead>
          <tr>
            <th className="tabla__titulo">
              <span className="tabla__cabecera"><span aria-hidden="true" className="panel__tipo">Aa</span> Nombre</span>
            </th>
            {columnas.map((c) => (
              <CabeceraDeColumna key={c.id} {...p} columna={c} />
            ))}
            <th className="tabla__añadir">
              {editable && (
                <button ref={botonAñadir} className="tabla__mas" onClick={() => setAñadiendo((v) => !v)}
                        aria-label="Añadir propiedad" title="Añadir propiedad">＋</button>
              )}
            </th>
          </tr>
        </thead>
        <tbody>
          {filas.map((f) => (
            <tr key={f.id} className="tabla__fila">
              <td className="tabla__titulo">
                <div className="tabla__nombre">
                  <button className="tabla__abrir" onClick={() => p.alAbrir(f.id)} aria-label="Abrir fila" title="Abrir">
                    <IconoDePagina icono={f.icon} tam={16} />
                  </button>
                  <TituloEditable titulo={f.title} editable={editable}
                                  alCambiar={(t) => p.alCambiarFila(f.id, { title: t })} />
                  <button className="tabla__ver" onClick={() => p.alAbrir(f.id)}>ABRIR</button>
                  {editable && (
                    <button className="tabla__borrar" onClick={() => p.alBorrarFila(f.id)}
                            aria-label="Mover la fila a la papelera" title="Mover a la papelera">🗑</button>
                  )}
                </div>
              </td>
              {columnas.map((c) => (
                <td key={c.id} className={`tabla__celda tabla__celda--${c.type}`}>
                  <Celda prop={c} valor={f.props[c.id]} editable={editable}
                         alCambiar={(valor) => p.alCambiarFila(f.id, { props: { [c.id]: valor } })}
                         alCrearOpcion={editable ? p.alCrearOpcion : undefined} />
                </td>
              ))}
              <td />
            </tr>
          ))}
        </tbody>
      </table>
      {filas.length === 0 && (
        <div className="bd__vacio">
          {(vista.filters?.length ?? 0) > 0
            ? 'Ninguna fila cumple los filtros.'
            : editable ? 'Todavía no hay filas.' : 'Esta base está vacía.'}
        </div>
      )}
      {editable && (
        <button className="tabla__nueva" onClick={() => p.alNuevaFila()}>＋ Nueva fila</button>
      )}
      <div className="bd__pie">{filas.length} {filas.length === 1 ? 'fila' : 'filas'}</div>

      {añadiendo && (
        <FormularioDePropiedad ancla={botonAñadir} alCerrar={() => setAñadiendo(false)}
          alCrear={(nombre, tipo) => {
            p.alCambiarEsquema({ ...esquema, properties: [...esquema.properties, propiedadNueva(nombre, tipo)] })
            setAñadiendo(false)
          }} />
      )}
    </div>
  )
}

function TituloEditable({ titulo, editable, alCambiar }: {
  titulo: string; editable: boolean; alCambiar: (t: string) => void
}) {
  const [texto, setTexto] = useState(titulo)
  const [previo, setPrevio] = useState(titulo)
  if (titulo !== previo) {
    // El título cambió desde fuera (otra pestaña, un refresco): se sigue al servidor.
    setPrevio(titulo)
    setTexto(titulo)
  }
  return (
    <input
      className="celda__entrada tabla__entrada-titulo"
      value={texto}
      readOnly={!editable}
      placeholder="Sin título"
      onChange={(e) => setTexto(e.target.value)}
      onBlur={() => texto !== titulo && alCambiar(texto)}
      onKeyDown={(e) => e.key === 'Enter' && (e.target as HTMLInputElement).blur()}
      aria-label="Nombre de la fila"
      maxLength={200}
    />
  )
}

function FormularioDePropiedad({ ancla, alCrear, alCerrar }: {
  ancla: React.RefObject<HTMLElement | null>; alCrear: (nombre: string, tipo: TipoProp) => void; alCerrar: () => void
}) {
  const [nombre, setNombre] = useState('')
  return (
    <Flotante ancla={ancla} alCerrar={alCerrar} ancho={280} alinear="fin">
      <div className="panel">
        <input autoFocus value={nombre} placeholder="Nombre de la propiedad" aria-label="Nombre"
               onChange={(e) => setNombre(e.target.value)} />
        <div className="panel__tipos">
          {TIPOS.map((t) => (
            <button key={t.tipo} className="menu__opcion" onClick={() => alCrear(nombre || t.nombre, t.tipo)}>
              <span className="menu__icono" aria-hidden="true">{t.icono}</span>{t.nombre}
            </button>
          ))}
        </div>
      </div>
    </Flotante>
  )
}

function CabeceraDeColumna({ columna, esquema, vista, editable, alCambiarEsquema, alCambiarVista }:
  AccionesDeVista & { columna: Propiedad }) {
  const [menu, setMenu] = useState(false)
  const [renombrando, setRenombrando] = useState(false)
  const [opciones, setOpciones] = useState(false)
  const ancla = useRef<HTMLButtonElement>(null)
  const ordenada = vista.sort?.prop === columna.id ? vista.sort.dir : null
  const conOpciones = columna.type === 'select' || columna.type === 'multi_select' || columna.type === 'status'

  const cambiarColumna = (nueva: Propiedad) =>
    alCambiarEsquema({ ...esquema, properties: esquema.properties.map((c) => (c.id === nueva.id ? nueva : c)) })

  return (
    <th className="tabla__col">
      {renombrando ? (
        <input autoFocus className="bd__renombrar" defaultValue={columna.name} aria-label="Nombre de la propiedad"
               onBlur={(e) => {
                 setRenombrando(false)
                 const n = e.target.value.trim()
                 if (n && n !== columna.name) cambiarColumna({ ...columna, name: n })
               }}
               onKeyDown={(e) => (e.key === 'Enter' || e.key === 'Escape') && (e.target as HTMLInputElement).blur()} />
      ) : (
        <button ref={ancla} className="tabla__cabecera" onClick={() => setMenu((v) => !v)} aria-haspopup="menu">
          <span aria-hidden="true" className="panel__tipo">{ICONO_DE_TIPO[columna.type]}</span>
          <span className="tabla__nombre-col">{columna.name}</span>
          {ordenada && <span aria-label={ordenada === 'asc' ? 'ascendente' : 'descendente'}>{ordenada === 'asc' ? '↑' : '↓'}</span>}
        </button>
      )}
      {menu && (
        <Menu ancla={ancla} alCerrar={() => setMenu(false)} ancho={230} opciones={[
          { texto: 'Orden ascendente', icono: '↑', alElegir: () => alCambiarVista({ ...vista, sort: { prop: columna.id, dir: 'asc' } }) },
          { texto: 'Orden descendente', icono: '↓', alElegir: () => alCambiarVista({ ...vista, sort: { prop: columna.id, dir: 'desc' } }) },
          ...(ordenada ? [{ texto: 'Quitar orden', icono: '✕', alElegir: () => alCambiarVista({ ...vista, sort: undefined }) }] : []),
          { texto: 'Ocultar en esta vista', icono: '◌', alElegir: () => alCambiarVista({ ...vista, hidden: [...(vista.hidden ?? []), columna.id] }) },
          ...(editable ? [
            { separador: true, texto: '' },
            { texto: 'Renombrar', icono: '✎', alElegir: () => setRenombrando(true) },
            ...(conOpciones ? [{ texto: 'Editar opciones', icono: '◉', alElegir: () => setOpciones(true) }] : []),
            {
              texto: 'Eliminar propiedad', icono: '🗑', peligro: true,
              alElegir: () => alCambiarEsquema(quitarPropiedad(esquema, columna.id)),
            },
          ] : []),
        ]} />
      )}
      {opciones && <EditorDeOpciones ancla={ancla} columna={columna} alCambiar={cambiarColumna} alCerrar={() => setOpciones(false)} />}
    </th>
  )
}

/** Quita la propiedad y todo lo que la mencionaba en las vistas, para no dejar referencias rotas. */
function quitarPropiedad(esquema: Esquema, id: string): Esquema {
  return {
    properties: esquema.properties.filter((c) => c.id !== id),
    views: esquema.views.map((v) => ({
      ...v,
      group_by: v.group_by === id ? undefined : v.group_by,
      sort: v.sort?.prop === id ? undefined : v.sort,
      filters: v.filters?.filter((f) => f.prop !== id),
      hidden: v.hidden?.filter((h) => h !== id),
    })),
  }
}

function EditorDeOpciones({ ancla, columna, alCambiar, alCerrar }: {
  ancla: React.RefObject<HTMLElement | null>; columna: Propiedad; alCambiar: (c: Propiedad) => void; alCerrar: () => void
}) {
  const opciones = columna.options ?? []
  const poner = (lista: typeof opciones) => alCambiar({ ...columna, options: lista })
  return (
    <Flotante ancla={ancla} alCerrar={alCerrar} ancho={300}>
      <div className="panel">
        <div className="panel__titulo">Opciones de «{columna.name}»</div>
        {opciones.map((o, i) => (
          <div className="panel__fila" key={o.id}>
            <button className={`pastilla pastilla--${o.color} pastilla--muestra`}
                    aria-label={`Color: ${o.color}. Pulsa para cambiarlo`}
                    onClick={() => poner(opciones.map((x) => x.id === o.id
                      ? { ...x, color: COLORES[(COLORES.indexOf(x.color as never) + 1) % COLORES.length] } : x))}>
              ●
            </button>
            <input defaultValue={o.name} aria-label="Nombre de la opción" maxLength={40}
                   onBlur={(e) => {
                     const n = e.target.value.trim()
                     if (n && n !== o.name) poner(opciones.map((x) => (x.id === o.id ? { ...x, name: n } : x)))
                   }} />
            <button className="arbol-boton arbol-boton--visible" aria-label="Quitar opción"
                    onClick={() => poner(opciones.filter((_, j) => j !== i))}>✕</button>
          </div>
        ))}
        <button className="boton boton--plano panel__añadir"
                onClick={() => poner([...opciones, { id: 'a' + Math.random().toString(36).slice(2, 8).padEnd(6, '0'), name: 'Nueva opción', color: COLORES[opciones.length % COLORES.length] }])}>
          ＋ Añadir opción
        </button>
      </div>
    </Flotante>
  )
}
